import { useLocale } from '@iris/i18n/react';
import { useAuth } from '@iris/ui/auth/auth-provider';
import { RouteFallback } from '@iris/ui/components/app-screens';
import { BackButton, parentPath } from '@iris/ui/components/back-button';
import { BrandMark } from '@iris/ui/components/brand-mark';
import { LanguageSwitch } from '@iris/ui/components/language-switch';
import { OfflineBanner } from '@iris/ui/components/offline-banner';
import { PhoneFrame } from '@iris/ui/components/phone-frame';
import { cn } from '@iris/ui/lib/utils';
import { CalendarClock, ScanLine, UserRound } from 'lucide-react';
import { Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';

import { SubscriptionBanner, SuspendedBanner } from './billing/billing-kit';
import { SECTIONS, StaffSidebar, StaffTabs } from './nav';
import { isOperating, useShops } from './shop-context';
import { ShopName } from './shop-name';
import { UpdatePrompt } from './update-prompt';

/** Screens reached from the bottom bar, the wizard and the sign-in flows: no back arrow (ADR 0011 D6). */
const TOP_LEVEL = [
  '/',
  '/dashboard',
  '/scan',
  '/today',
  '/more',
  '/account',
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
];
/** On a phone, the screens of the "Plus" menu go back to it. */
const PARENTS = [[/^\/(promotions|programs|rewards|alerts|staff|shop|billing|settings)$/, '/more']] as const;

/**
 * App frame: offline banner, brand and language switch; the cashier's bottom bar, or for owner and manager of an
 * active shop the bottom bar on phone and the sidebar on desktop (T4 §1.2). The brand name comes from one key.
 */
export function Layout() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { changeLocale, status } = useAuth();
  const { current } = useShops();
  // T4 §1.2: the cashier's bottom bar (Scanner · Aujourd'hui · Compte)
  const cashierTabs = current?.role === 'CASHIER' && isOperating(current.status);
  const staffNav = current && current.role !== 'CASHIER' && isOperating(current.status) ? current.role : null;
  // ADR 0014: the cashier's screens stay phone-sized, so their bar keeps its phone layout on a computer too
  const phone = current?.role === 'CASHIER';
  const { pathname } = useLocation();
  const wizard = pathname.startsWith('/onboarding') || pathname.startsWith('/invite/');
  const parent = wizard ? null : parentPath(pathname, TOP_LEVEL, PARENTS);
  // with the sidebar (owner and manager on a wide screen) its own screens need no arrow
  const inSidebar = staffNav !== null && SECTIONS.some((section) => section.to === pathname);

  useEffect(() => {
    document.title = t('brand.name');
  }, [t, locale]);

  const signedIn = status === 'authenticated' || status === 'offline';
  const language = (
    <LanguageSwitch
      value={locale}
      onChange={changeLocale}
      label={t('common.language.label')}
      names={{ fr: t('common.language.fr'), ar: t('common.language.ar') }}
    />
  );

  return (
    <PhoneFrame enabled={phone}>
      <div
        className={cn(
          'flex min-h-dvh bg-neutral-50 print:block print:min-h-0 print:bg-white',
          phone && 'md:min-h-full',
        )}
      >
        {staffNav ? (
          <StaffSidebar
            role={staffNav}
            footer={
              <div className="flex items-center justify-between gap-2">
                {language}
                <Link
                  to="/account"
                  aria-label={t('profile.title')}
                  className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <UserRound aria-hidden className="h-4 w-4" />
                </Link>
              </div>
            }
          />
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col">
          <OfflineBanner message={t('common.offline')} />
          <UpdatePrompt />
          {/* phone and tablet bar; on a computer the sidebar carries brand, shop, language and account */}
          <header
            className={cn(
              'sticky top-0 z-30 bg-neutral-50/80 backdrop-blur-md supports-[backdrop-filter]:bg-neutral-50/70 print:hidden',
              staffNav && 'lg:hidden',
            )}
          >
            <div className="mx-auto flex w-full max-w-[1200px] flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-2.5">
              <div className="flex items-center gap-1">
                {parent ? <BackButton label={t('common.back')} fallback={parent} /> : null}
                <Link
                  to="/"
                  className="flex items-center gap-2 text-lg font-semibold tracking-tight text-primary"
                >
                  <BrandMark className="h-7 w-7" />
                  {t('brand.name')}
                </Link>
              </div>
              <div className={cn('order-last w-full', !phone && 'sm:order-none sm:ms-auto sm:w-auto')}>
                <ShopName />
              </div>
              {language}
              {signedIn ? (
                <Link
                  to="/account"
                  aria-label={t('profile.title')}
                  className="flex h-9 w-9 items-center justify-center rounded-full border hover:bg-muted"
                >
                  <UserRound aria-hidden className="h-4 w-4" />
                </Link>
              ) : null}
            </div>
          </header>
          <main
            className={cn(
              'mx-auto w-full max-w-[1200px] flex-1 px-4 py-6 print:max-w-none print:p-0',
              staffNav && 'lg:px-10 lg:py-8',
              (cashierTabs || staffNav) && 'pb-24 lg:pb-8',
            )}
          >
            {staffNav && parent && !inSidebar ? (
              <div className="-ms-2 mb-4 hidden lg:block print:hidden">
                <BackButton label={t('common.back')} fallback={parent} />
              </div>
            ) : null}
            {current?.status === 'SUSPENDED' ? <SuspendedBanner /> : staffNav ? <SubscriptionBanner /> : null}
            <Suspense fallback={<RouteFallback label={t('common.loading')} />}>
              <div
                key={pathname}
                className="animate-in fade-in-0 slide-in-from-bottom-1 duration-300 motion-reduce:animate-none"
              >
                <Outlet />
              </div>
            </Suspense>
          </main>
        </div>
        {cashierTabs ? <CashierTabs /> : null}
        {staffNav ? <StaffTabs /> : null}
      </div>
    </PhoneFrame>
  );
}

function CashierTabs() {
  const { t } = useTranslation();
  const tab = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex flex-1 flex-col items-center gap-1 py-2 text-xs',
      isActive ? 'font-semibold text-primary' : 'text-muted-foreground',
    );
  return (
    <nav
      aria-label={t('nav.label')}
      className="fixed inset-x-0 bottom-0 z-40 border-t bg-background pb-[env(safe-area-inset-bottom)]"
    >
      <div className="mx-auto flex max-w-lg">
        <NavLink to="/scan" className={tab}>
          <ScanLine aria-hidden className="h-5 w-5" />
          {t('counter.title')}
        </NavLink>
        <NavLink to="/today" className={tab}>
          <CalendarClock aria-hidden className="h-5 w-5" />
          {t('today.title')}
        </NavLink>
        <NavLink to="/account" className={tab}>
          <UserRound aria-hidden className="h-5 w-5" />
          {t('profile.title')}
        </NavLink>
      </div>
    </nav>
  );
}
